import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { JourneyModel, RepeatingFieldMapping } from '../models/journey.model';

/**
 * JourneyMappingService
 *
 * This service handles field mappings between different services in a multi-step journey.
 *. APPLYING MAPPINGS: Services receive and apply mappings to their form fields
 *    - SingleServiceRenderComponent subscribes to the fieldMapping$ Observable
 *    - When it receives a mapping event for its service, it calls applyFieldMapping()
 *    - When a service is first loaded or re-enabled, it calls applyAllMappings() to apply all stored mappings
 *    - Mapped fields are made readonly to prevent user modification
 *
 * The principle is simple: when any source field changes, its value is propagated to all mapped target fields,
 * and those fields become readonly. This ensures data consistency across the multi-step journey.
 */
export interface FieldMappingEvent {
  targetServiceCode: string;
  targetField: string;
  sourceValue: any;
}

@Injectable({
  providedIn: 'root',
})
export class JourneyMappingService {
  private readonly journeyDataSubject =
    new BehaviorSubject<JourneyModel | null>(null);
  private readonly fieldMappingSubject =
    new BehaviorSubject<FieldMappingEvent | null>(null);

  // Store all mappings for each service
  private readonly serviceMappings: Map<string, FieldMappingEvent[]> =
    new Map();

  public journeyData$: Observable<JourneyModel | null> =
    this.journeyDataSubject.asObservable();
  public fieldMapping$: Observable<FieldMappingEvent | null> =
    this.fieldMappingSubject.asObservable();

  /**
   * Set the journey mapping data
   */
  setJourneyData(journeyData: JourneyModel): void {
    this.journeyDataSubject.next(journeyData);
  }

  /**
   * Get the current journey data
   */
  getJourneyData(): JourneyModel | null {
    return this.journeyDataSubject.value;
  }

  /**
   * Notify that a source field has changed and needs to be propagated to target fields
   */
  updateFieldMapping(
    targetServiceCode: string,
    targetField: string,
    sourceValue: any
  ): void {
    const mappingEvent: FieldMappingEvent = {
      targetServiceCode,
      targetField,
      sourceValue,
    };

    // Store the mapping
    this.storeMapping(mappingEvent);

    // Emit the event for immediate consumption
    this.fieldMappingSubject.next(mappingEvent);
  }

  /**
   * Store a mapping for a service
   * This ensures we maintain a record of all mappings that have been applied
   */
  private storeMapping(mapping: FieldMappingEvent): void {
    const serviceCode = mapping.targetServiceCode;

    if (!this.serviceMappings.has(serviceCode)) {
      this.serviceMappings.set(serviceCode, []);
    }

    // Get the existing mappings for this service
    const serviceMappings = this.serviceMappings.get(serviceCode) || [];

    // Check if we already have a mapping for this field
    const existingIndex = serviceMappings.findIndex(
      m => m.targetField === mapping.targetField
    );

    if (existingIndex >= 0) {
      // Replace the existing mapping with the new one
      serviceMappings[existingIndex] = mapping;
    } else {
      // Add the new mapping
      serviceMappings.push(mapping);
    }

    // Update the map
    this.serviceMappings.set(serviceCode, serviceMappings);
  }

  /**
   * Get all mappings for a service
   */
  getMappingsForService(serviceCode: string): FieldMappingEvent[] {
    return this.serviceMappings.get(serviceCode) || [];
  }

  /**
   * Get field mappings configuration for a specific service from the journey model
   */
  getFieldMappingConfigForService(
    serviceCode: string
  ): RepeatingFieldMapping[] {
    const journeyData = this.journeyDataSubject.value;
    if (!journeyData?.serviceSequence) {
      return [];
    }

    const serviceDefinition = journeyData.serviceSequence.find(
      def => def.serviceCode === serviceCode
    );

    if (!serviceDefinition?.repeatingFieldsMapping) {
      return [];
    }

    return serviceDefinition.repeatingFieldsMapping;
  }

  /**
   * Helper method to extract a value from a source model by field path
   * This handles both flat keys and nested paths
   */
  getFieldValueFromModel(model: any, fieldPath: string): any {
    if (!model) return undefined;

    // find the field in a flattened model
    const flattenedModel = this.flattenModel(model);

    // First try exact path match
    if (flattenedModel[fieldPath] !== undefined) {
      return flattenedModel[fieldPath];
    }

    // Then try finding keys that end with the field path
    const matchingPath = Object.keys(flattenedModel).find(
      path => path.endsWith(`.${fieldPath}`) || path === fieldPath
    );

    // Return the value if found, otherwise undefined
    return matchingPath ? flattenedModel[matchingPath] : undefined;
  }

  /**
   * Flattens a nested model structure into a single-level object with dot notation
   * Example: { a: { b: { c: 1 } } } becomes { 'a.b.c': 1 }
   */
  private flattenModel(obj: any, prefix = ''): Record<string, any> {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return {};
    }

    return Object.keys(obj).reduce((acc: Record<string, any>, key: string) => {
      const prefixedKey = prefix ? `${prefix}.${key}` : key;

      // Skip special properties
      if (key === '__formLayout') {
        return acc;
      }

      if (
        typeof obj[key] === 'object' &&
        obj[key] !== null &&
        !Array.isArray(obj[key])
      ) {
        // Recursively flatten nested objects
        Object.assign(acc, this.flattenModel(obj[key], prefixedKey));
      } else {
        // Store the value with its full path
        acc[prefixedKey] = obj[key];
      }
      return acc;
    }, {});
  }
}
